import { useState } from "react";
import ReactMarkdown from "react-markdown";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Button } from "@mui/material";
import MarkdownModal from "datasets/components/MarkdownModal";
import useDataset from "datasets/hooks/useDataset";
import Dataset from "datasets/types/interfaces/dataset";
import remarkGfm from "remark-gfm";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const DescriptionEditor = ({ dataset, canEdit }: { dataset: Dataset; canEdit: boolean }) => {
  const { mutate } = useDataset();

  const [descriptionError, setDescriptionError] = useState("");
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [updatingDescription, setUpdatingDescription] = useState(false);

  const updateDescription = async (description: string) => {
    setDescriptionError("");
    setUpdatingDescription(true);

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset?.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description }),
      });

      if (!resp.ok) {
        throw new Error("Unable to update dataset description");
      }

      const datasetResp: Dataset = await resp.json();

      trackEvent("dataset_description_updated", {
        dataset_id: datasetResp.id,
        dataset_description: datasetResp.description,
      });

      mutate();
      setDescriptionModalOpen(false);
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setDescriptionError(descriptionError);
    }

    setUpdatingDescription(false);
  };

  return (
    <Box
      sx={{
        lineHeight: 1.625,
        py: 1.5,
        borderRadius: 2,
        position: "relative",
        ...(canEdit && {
          "&:hover .description-edit-button, &:focus-within .description-edit-button": {
            opacity: 1,
          },
        }),
      }}
    >
      {canEdit && (
        <Button
          size="small"
          variant="contained"
          startIcon={<EditOutlinedIcon fontSize="small" />}
          onClick={() => setDescriptionModalOpen(true)}
          className="description-edit-button"
          sx={{
            position: "absolute",
            top: 12,
            right: 8,
            zIndex: 1,
            textTransform: "none",
            height: 32,
            minHeight: 32,
            minWidth: "auto",
            px: 1,
            opacity: { xs: 1, sm: 0 },
            transition: "opacity 150ms ease-in-out",
            bgcolor: "grey.200",
            color: "grey.800",
            boxShadow: "none",
            "&:hover": { bgcolor: "grey.300", color: "grey.900", boxShadow: "none" },
          }}
        >
          Edit
        </Button>
      )}
      <Box
        className="prose prose-sm max-w-none"
        sx={{
          img: {
            width: "100%",
          },
          "> :first-child": {
            mt: 0,
            pt: 0,
          },
          "> :last-child": {
            mb: 0,
            pb: 0,
          },
          "&:empty": {
            "&::before": {
              content: '"Description"',
              opacity: 0.5,
            },
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{dataset.description}</ReactMarkdown>
      </Box>
      {canEdit && descriptionModalOpen && (
        <MarkdownModal
          error={descriptionError}
          loading={updatingDescription}
          onClose={() => setDescriptionModalOpen(false)}
          onSubmit={updateDescription}
          open={descriptionModalOpen}
          title="Edit Description"
          value={dataset.description}
        />
      )}
    </Box>
  );
};

export default DescriptionEditor;
